// Regras puras da tela Filas & Chatbot (/queues).
// Separadas dos componentes para ficarem testáveis sem montar o React.
import { i18n } from "../../translate/i18n";

// Ordem dos dias como o formulário de horários grava (`weekdayEn`).
export const WEEKDAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const temHorario = (dia) => !!(dia?.startTimeA && dia?.endTimeA);

// "08:00" a partir de "08:00" ou "08:00:00".
const hhmm = (hora) => {
  if (!hora) return "";
  const partes = String(hora).split(":");
  return partes.length >= 2 ? `${partes[0]}:${partes[1]}` : String(hora);
};

/**
 * Resumo do horário da fila para a coluna da listagem: "24h",
 * "Seg–Sex 08–18h" ou "Sem horário".
 *
 * Nenhum dia preenchido significa que a fila atende sempre — é assim que o
 * backend trata a ausência de `schedules`, e escrever "Sem horário" ali
 * sugeriria o contrário. "Sem horário" fica para quando existe configuração
 * mas nenhum dia tem faixa válida.
 */
export const scheduleSummary = (schedules) => {
  if (!Array.isArray(schedules) || schedules.length === 0) {
    return { label: i18n.t("aeros.queues.schedule.always"), always: true };
  }

  const abertos = schedules.filter(temHorario);
  if (abertos.length === 0) {
    return { label: i18n.t("aeros.queues.schedule.none"), always: false };
  }
  if (abertos.length === schedules.length && schedules.length >= 7) {
    return { label: i18n.t("aeros.queues.schedule.always"), always: true };
  }

  const indices = abertos
    .map((dia) => WEEKDAYS.indexOf(dia.weekdayEn))
    .filter((i) => i >= 0)
    .sort((a, b) => a - b);

  const primeiro = indices[0];
  const ultimo = indices[indices.length - 1];
  const faixa =
    primeiro === ultimo
      ? i18n.t(`aeros.queues.weekdayShort.${WEEKDAYS[primeiro]}`)
      : `${i18n.t(`aeros.queues.weekdayShort.${WEEKDAYS[primeiro]}`)}–${i18n.t(
          `aeros.queues.weekdayShort.${WEEKDAYS[ultimo]}`
        )}`;

  // Mostra a abertura mais cedo e o fechamento mais tarde da semana: a coluna
  // tem uma linha, e detalhar dia a dia é papel da aba Horários.
  const inicio = abertos
    .map((d) => hhmm(d.startTimeA))
    .filter(Boolean)
    .sort()[0];
  const fim = abertos
    .map((d) => hhmm(d.endTimeB || d.endTimeA))
    .filter(Boolean)
    .sort()
    .pop();

  return { label: `${faixa} ${inicio}–${fim}`, always: false };
};

// "6 atendentes · 12 abertos" — subtítulo do nome da fila.
export const queueSubtitle = ({ userCount = 0, openCount = 0 } = {}) =>
  [
    i18n.t(
      userCount === 1 ? "aeros.queues.agentOne" : "aeros.queues.agents",
      { count: userCount }
    ),
    i18n.t(
      openCount === 1 ? "aeros.queues.openOne" : "aeros.queues.open",
      { count: openCount }
    ),
  ].join(" · ");

// "4 opções" ou "sem chatbot" na coluna Chatbot.
export const botLabel = (chatbotCount = 0) =>
  chatbotCount > 0
    ? i18n.t(
        chatbotCount === 1 ? "aeros.queues.optionOne" : "aeros.queues.options",
        { count: chatbotCount }
      )
    : i18n.t("aeros.queues.noBot");

/**
 * Totais da faixa de KPIs.
 *
 * `pending` soma o que está aguardando POR FILA — é o que o rótulo promete
 * ("em todas as filas"). Ticket sem fila não entra nessa conta, e é correto:
 * ele não está esperando em fila nenhuma.
 */
export const kpiTotals = (queues = []) => ({
  total: queues.length,
  withBot: queues.filter((q) => Number(q.chatbotCount) > 0).length,
  pending: queues.reduce((soma, q) => soma + (Number(q.pendingCount) || 0), 0),
});

/**
 * Nova ordem depois de arrastar, renumerada a partir de 1.
 *
 * O cliente calcula e manda pronto para `PUT /queue/order`; numerar com
 * buracos faria o menu do bot pular números na próxima leitura.
 */
export const reorderQueues = (queues = [], de, para) => {
  const valido =
    Number.isInteger(de) &&
    Number.isInteger(para) &&
    de >= 0 &&
    para >= 0 &&
    de < queues.length &&
    para < queues.length;

  const lista = [...queues];
  if (valido && de !== para) {
    const [movido] = lista.splice(de, 1);
    lista.splice(para, 0, movido);
  }
  return lista.map((fila, i) => ({ ...fila, orderQueue: i + 1 }));
};

// Só o que o `PUT /queue/order` precisa receber.
export const orderPayload = (queues = []) =>
  queues.map((fila, i) => ({ id: fila.id, orderQueue: i + 1 }));
